import React, { useEffect, useState } from 'react';
import { Box, Tab, Tabs, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { FrameworkList } from './FrameworkList';
import { SettingsTabPanel } from '..';
import '../../../../styles/pages/admin/settings/skillFramework.scss';
import { SkillDevelopment } from '../../../../config/svg/CommonSvgIcon';
import { SkillFrameworkDrawer } from './FrameworkList/SkillFrameworkDrawer';
import { useDispatch } from 'react-redux';
import { Enums } from '../../../../config/enums';
import {
	createSkillFramework,
	deleteSkillFramework,
	frameworkList,
	setRelaodSkillFramework,
	updateSkillFramework,
} from '../../../../action/adminSettings';
import { SkillDrawer } from './SkillList/SkillDrawer';
import { useSnackbar } from 'notistack';
import { SkillList } from './SkillList';

export const SkillDevelopmentSetting: React.FC<any> = (props: any) => {
	const {
		tabSelected,
		a11yProps,
		handleTabChange,
		skillFrameworkOpen,
		setSkillFrameworkOpen,
		skillDrawerOpen,
		setSkillDrawerOpen,
	} = props;
	const { t } = useTranslation();
	const dispatch = useDispatch();
	// State to manage form data
	const defaultFramework = {
		skillFrameworkId: 0,
		frameworkName: '',
		description: '',
		levels: [
			{ name: '', description: '', isActive: true },
			{ name: '', description: '', isActive: false },
		],
		proficiencyScales: [{ proficiencyScaleName: '', proficiencyScaleDescription: '' }],
	};
	const [skillFramework, setSkillFramework] = useState<any>(defaultFramework);
	const defaultFrameworkError = {
		frameworkName: '',
		description: '',
		levels: [{ name: '', description: '' }],
		proficiencyScales: [{ proficiencyScaleName: '', proficiencyScaleDescription: '' }],
	};
	const [frameworkError, setFrameworkError] = useState<any>(defaultFrameworkError);

	useEffect(() => {
		if (!skillFrameworkOpen) {
			setFrameworkError({ ...defaultFrameworkError });
			setSkillFramework({ ...defaultFramework });
		}
	}, [skillFrameworkOpen]);
	const { enqueueSnackbar } = useSnackbar();
	const showApiMsgs = (msg: string, variant: any) => {
		enqueueSnackbar(msg, {
			variant: variant,
			autoHideDuration: 5000,
		});
	};
	const resetTabDetailsAndChange = (event: any, newValue: Number) => {
		handleTabChange(event, newValue);
	};

	// Handle input changes for framework name and description
	const handleOnChange = (name: string, value: string) => {
		setSkillFramework((prev: any) => ({ ...prev, [name]: value }));
		setFrameworkError({ ...frameworkError, frameworkName: '' });
	};

	const handleSaveFramework = async () => {
		let errorData = { ...frameworkError };
		let isError = false;
		if (!skillFramework.frameworkName || !skillFramework.frameworkName.trim()) {
			errorData.frameworkName = 'Framework name is required.';
			isError = true;
		}
		if (!skillFramework.levels[0]?.name.trim()) {
			errorData.levels[0].name = 'Level 1 must have name.';
			isError = true;
		}
		if (!skillFramework.levels[0]?.description.trim()) {
			errorData.levels[0].description = 'Level 1 must have description.';
			isError = true;
		}
		if (skillFramework.proficiencyScales.length < 2) {
			errorData.proficiencyScalesCount = 'Proficiency scale should have at least 2 entries.';
			isError = true;
		}
		skillFramework.proficiencyScales.forEach((p: any, index: number) => {
			if (!errorData.proficiencyScales[index]) {
				errorData.proficiencyScales[index] = { proficiencyScaleName: '', proficiencyScaleDescription: '' };
			}
			if (!p.proficiencyScaleName.trim()) {
				errorData.proficiencyScales[index].proficiencyScaleName = 'Proficiency scale name is required.';
				isError = true;
			}
			if (!p.proficiencyScaleDescription.trim()) {
				errorData.proficiencyScales[index].proficiencyScaleDescription = 'Proficiency scale description is required.';
				isError = true;
			}
		});
		if (isError) {
			setFrameworkError(errorData);
			return;
		}

		const frameworkData = {
			skillFrameworkId: skillFramework.skillFrameworkId || 0,
			frameworkName: skillFramework.frameworkName,
			description: skillFramework.description || '',
			isLevelOneActive: !!(skillFramework.levels[0]?.name.trim() && skillFramework.levels[0]?.description.trim()),
			levelOneName: skillFramework.levels[0]?.name || '',
			levelOneDescription: skillFramework.levels[0]?.description || '',
			isLevelTwoActive: !!(skillFramework.levels[1]?.name.trim() && skillFramework.levels[1]?.description.trim()),
			levelTwoName: skillFramework.levels[1]?.name || '',
			levelTwoDescription: skillFramework.levels[1]?.description || '',
			isLevelThreeActive: !!(skillFramework.levels[2]?.name.trim() && skillFramework.levels[2]?.description.trim()),
			levelThreeName: skillFramework.levels[2]?.name || '',
			levelThreeDescription: skillFramework.levels[2]?.description || '',
			isLevelFourActive: !!(skillFramework.levels[3]?.name.trim() && skillFramework.levels[3]?.description.trim()),
			levelFourName: skillFramework.levels[3]?.name || '',
			levelFourDescription: skillFramework.levels[3]?.description || '',
			isLevelFiveActive: !!(skillFramework.levels[4]?.name.trim() && skillFramework.levels[4]?.description.trim()),
			levelFiveName: skillFramework.levels[4]?.name || '',
			levelFiveDescription: skillFramework.levels[4]?.description || '',
			isLevelSixActive: !!(skillFramework.levels[5]?.name.trim() && skillFramework.levels[5]?.description.trim()),
			levelSixName: skillFramework.levels[5]?.name || '',
			levelSixDescription: skillFramework.levels[5]?.description || '',
			proficiencyScales: skillFramework.proficiencyScales.map((p: any, idx: number) => ({
				proficiencyScaleId: p.proficiencyScaleGroupMappingId || 0,
				proficiencyScaleName: p.proficiencyScaleName || '',
				proficiencyScaleDescription: p.proficiencyScaleDescription || '',
				sequence: p.sequence || idx,
			})),
		};

		try {
			let response: any;
			if (skillFramework.skillFrameworkId && skillFramework.skillFrameworkId !== 0) {
				// Edit mode: call PUT API
				response = await dispatch(updateSkillFramework(frameworkData));
			} else {
				// Create mode: call POST API
				response = await dispatch(createSkillFramework(frameworkData));
			}
			if (response && response?.data?.status === Enums.STATUS_SUCCESS) {
				showApiMsgs(
					skillFramework.skillFrameworkId && skillFramework.skillFrameworkId !== 0
						? t('Skill Framework updated successfully!')
						: t('Skill Framework created successfully!'),
					'success'
				);
				setSkillFrameworkOpen({ open: false });
				setSkillFramework({ ...defaultFramework });
				dispatch(setRelaodSkillFramework(true));
			} else {
				console.error('API Error Response:', response?.data);
			}
		} catch (error: any) {
			console.error('API Request Failed:', error.response?.data || error.message);
		}
	};
	// const handleLevelChange = (index: number, field: string, value: string) => {
	// 	setFramework((prev) => {
	// 		const updatedLevels = [...prev.levels];
	// 		updatedLevels[index] = { ...updatedLevels[index], [field]: value };

	// 		// Enable next row if current row is filled and next row exists
	// 		if (
	// 			index < updatedLevels.length - 1 &&
	// 			updatedLevels[index].name.trim() &&
	// 			updatedLevels[index].description.trim()
	// 		) {
	// 			updatedLevels[index + 1] = { ...updatedLevels[index + 1], isActive: true };
	// 		}

	// 		// Find the last enabled level and its index
	// 		let lastEnabledIndex = -1;
	// 		let lastEnabledLevel = null;
	// 		for (let i = updatedLevels.length - 1; i >= 0; i--) {
	// 			if (updatedLevels[i].isActive) {
	// 				lastEnabledLevel = updatedLevels[i];
	// 				lastEnabledIndex = i;
	// 				break;
	// 			}
	// 		}

	// 		// Auto-add a new disabled level if the last enabled level is filled and < 6 levels
	// 		if (
	// 			updatedLevels.length < 6 &&
	// 			lastEnabledLevel &&
	// 			lastEnabledLevel.name.trim() &&
	// 			lastEnabledLevel.description.trim() &&
	// 			index === lastEnabledIndex
	// 		) {
	// 			updatedLevels.push({ name: '', description: '', isActive: false });
	// 		}

	// 		return { ...prev, levels: updatedLevels };
	// 	});
	// };

	const handleLevelChange = (index: number, field: string, value: string) => {
		setSkillFramework((prev: any) => {
			const updatedLevels = [...prev.levels];
			updatedLevels[index] = { ...updatedLevels[index], [field]: value };

			// Enable next level if current is filled
			if (
				index < 5 && // up to 6 levels (0-based)
				updatedLevels[index].name.trim() &&
				updatedLevels[index].description.trim()
			) {
				// Ensure next level exists
				if (!updatedLevels[index + 1]) {
					updatedLevels.push({ name: '', description: '', isActive: false });
				}
				updatedLevels[index + 1].isActive = true;
			}

			// Always keep at least 2 levels
			while (updatedLevels.length < 2) {
				updatedLevels.push({ name: '', description: '', isActive: false });
			}
			// Limit to 6 levels
			if (updatedLevels.length > 6) {
				updatedLevels.length = 6;
			}

			return { ...prev, levels: updatedLevels };
		});
		let updatedError = { ...frameworkError };
		if (updatedError.levels[index]) {
			updatedError.levels[index][field] = ''; // Clear error for this field
		}
		setFrameworkError(updatedError);
	};
	// Handle input changes for proficiency scales
	const handleProficiencyChange = (index: number, field: string, value: string) => {
		setSkillFramework((prev: any) => {
			const updatedProficiencies = [...prev.proficiencyScales];
			updatedProficiencies[index] = { ...updatedProficiencies[index], [field]: value };
			return { ...prev, proficiencyScales: updatedProficiencies };
		});
		let updatedError = { ...frameworkError };
		if (updatedError.proficiencyScales[index]) {
			updatedError.proficiencyScales[index][field] = ''; // Clear error for this field
		}
		// Also clear the count error if present
		if (updatedError.proficiencyScalesCount) {
			updatedError.proficiencyScalesCount = '';
		}
		setFrameworkError(updatedError);
	};

	// Add a new level (up to 6)
	const handleAddLevel = () => {
		if (skillFramework.levels.length < 6) {
			setSkillFramework((prev: any) => ({
				...prev,
				levels: [...prev.levels, { name: '', description: '', isActive: true }],
			}));
		}
	};

	// Remove a level (cannot remove the first level)
	const handleRemoveLevel = (index: number) => {
		if (index === 0) return; // Prevent removing the first level
		setSkillFramework((prev: any) => ({
			...prev,
			levels: prev.levels.filter((_: any, i: number) => i !== index),
		}));
	};

	// Add a new proficiency scale
	const handleAddProficiency = () => {
		setSkillFramework((prev: any) => ({
			...prev,
			proficiencyScales: [...prev.proficiencyScales, { proficiencyScaleName: '', proficiencyScaleDescription: '' }],
		}));
		setFrameworkError({
			...frameworkError,
			proficiencyScalesCount: '',
		});
	};

	// Remove a proficiency scale
	const handleRemoveProficiency = (index: number) => {
		setSkillFramework((prev: any) => ({
			...prev,
			proficiencyScales: prev.proficiencyScales.filter((_: any, i: number) => i !== index),
		}));
	};

	const handleEditFramework = (frameworkData: any) => {
		setSkillFramework({
			skillFrameworkId: frameworkData.skillFrameworkId || 0,
			frameworkName: frameworkData.name,
			description: frameworkData.description,
			levels: [
				{
					name: frameworkData.levelOneName || '',
					description: frameworkData.levelOneDescription || '',
					isActive: frameworkData.isLevelOneActive,
				},
				{
					name: frameworkData.levelTwoName || '',
					description: frameworkData.levelTwoDescription || '',
					isActive: frameworkData.isLevelTwoActive,
				},
				{
					name: frameworkData.levelThreeName || '',
					description: frameworkData.levelThreeDescription || '',
					isActive: frameworkData.isLevelThreeActive,
				},
				{
					name: frameworkData.levelFourName || '',
					description: frameworkData.levelFourDescription || '',
					isActive: frameworkData.isLevelFourActive,
				},
				{
					name: frameworkData.levelFiveName || '',
					description: frameworkData.levelFiveDescription || '',
					isActive: frameworkData.isLevelFiveActive,
				},
				{
					name: frameworkData.levelSixName || '',
					description: frameworkData.levelSixDescription || '',
					isActive: frameworkData.isLevelSixActive,
				},
			].filter((lvl) => lvl.name || lvl.description || lvl.isActive),
			proficiencyScales: (frameworkData.proficiencyScales || []).map((p: any) => ({
				proficiencyScaleId: p.proficiencyScaleId || 0,
				proficiencyScaleName: p.proficiencyScaleName || p.name || '',
				proficiencyScaleDescription: p.proficiencyScaleDescription || p.description || '',
				sequence: p.sequence || 0,
			})),
		});
		setSkillFrameworkOpen({ open: true });
	};

	const handleDeleteFramework = async (frameworkId: number) => {
		try {
			const response: any = await dispatch(deleteSkillFramework(frameworkId));
			if (response && response?.data?.status === Enums.STATUS_SUCCESS) {
				showApiMsgs(t('Skill Framework deleted successfully!'), 'success');
				dispatch(setRelaodSkillFramework(true));
			} else {
				showApiMsgs(response?.data?.messageList?.messageError || t('Failed to delete Skill Framework.'), 'error');
			}
		} catch (e: any) {
			console.error(e);
			showApiMsgs(e?.message || t('An error occurred while deleting.'), 'error');
		}
	};

	return (
		<>
			{skillFrameworkOpen && skillFrameworkOpen.open && (
				<SkillFrameworkDrawer
					skillFramework={skillFramework}
					frameworkError={frameworkError}
					setFrameworkError={setFrameworkError}
					handleOnChange={handleOnChange}
					handleLevelChange={handleLevelChange}
					handleAddLevel={handleAddLevel}
					handleRemoveLevel={handleRemoveLevel}
					handleProficiencyChange={handleProficiencyChange}
					handleAddProficiency={handleAddProficiency}
					handleRemoveProficiency={handleRemoveProficiency}
					handleSaveFramework={handleSaveFramework}
					skillFrameworkOpen={skillFrameworkOpen}
					setSkillFrameworkOpen={setSkillFrameworkOpen}
					{...props}
				/>
			)}
			{skillDrawerOpen && skillDrawerOpen.open && (
				<SkillDrawer
					handleSaveFramework={handleSaveFramework}
					handleSaveSkill={() => {
						handleTabChange({}, 1);
					}}
					skillDrawerOpen={skillDrawerOpen}
					setSkillDrawerOpen={setSkillDrawerOpen}
					{...props}
				/>
			)}
			<Box className='skill-development-area'>
				<Box className='admin-tabpanel-inner'>
					<Box className='admin-tabpanel-head'>
						<Box className='admin-tabpanel-head-left'>
							<Tabs
								className='okr-tab-button'
								value={tabSelected}
								onChange={resetTabDetailsAndChange}
								aria-label='Admin Tabs'
							>
								<Tab icon={<SkillDevelopment />} label={t('skillFramework')} value={0} {...a11yProps(0)}></Tab>
								<Tab icon={<SkillDevelopment />} label={t('skillLabel')} value={1} {...a11yProps(1)}></Tab>
							</Tabs>
						</Box>
						<Box className='admin-tabpanel-head-right'></Box>
					</Box>
					<SettingsTabPanel value={tabSelected} index={0}>
						<FrameworkList
							skillFramework={skillFramework}
							handleEditFramework={handleEditFramework}
							handleDeleteFramework={handleDeleteFramework}
							{...props}
						/>
					</SettingsTabPanel>
					<SettingsTabPanel value={tabSelected} index={1}>
						<SkillList {...props}></SkillList>
					</SettingsTabPanel>
				</Box>
			</Box>
		</>
	);
};
